import {
    Anchor,
    Box,
    Divider,
    IconChevronDownOutline,
    IconChevronUpOutline,
    token,
} from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import { GROUP_WIDTH_MAP } from '@zillow/zrs-cms';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SearchBox from './SearchBox';

const MenuLink = styled(Anchor)`
    font-family: ${token('fonts.sansSerif')};
    font-size: ${token('fontSizes.bodySmall')}px;
    font-weight: 600;
    line-height: 20px;
    color: ${(props) =>
        props.fontColor
            ? token(`colors.${props.fontColor}`)
            : token('colors.brand')};
    text-decoration: none;
    display: flex;
    height: 100%;
    align-items: center;
    &:visited {
        color: ${(props) =>
            props.fontColor
                ? token(`colors.${props.fontColor}`)
                : token('colors.brand')};
    }
    &:hover {
        color: ${token('colors.textDark')};
    }
`;

const MenuItem = ({ item, sx }) => (
    <Box
        as="li"
        sx={{
            ...sx,
        }}
    >
        <MenuLink href={item.url}>{item.label}</MenuLink>
    </Box>
);

const MenuList = ({ children, sx }) => (
    <Box as="ul" sx={{ listStyle: 'none', padding: '0', margin: '0', ...sx }}>
        {children}
    </Box>
);

const LearningCenterMenu = ({ router, menu }) => {
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    const ToggleIcon = open ? IconChevronUpOutline : IconChevronDownOutline;

    useEffect(() => {
        // Close menu if user clicks outside of menu
        const handleClickOutside = (event) => {
            if (open && !event.target.closest('#menu-toggle')) {
                setOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    });

    if (!menu?.items) {
        return null;
    }

    function parseMenu(items, root = 0) {
        return items
            .filter((item) => item.parent === root)
            .map((menuItem) => {
                let path;
                try {
                    const url = new URL(menuItem.url);
                    path = url.pathname;
                } catch (error) {
                    console.warn(`Failed to parse URL: ${menuItem.url}`);

                    if (menuItem.url.startsWith('#')) {
                        path = router.pathname;
                    } else {
                        path = '';
                    }
                }

                return {
                    id: menuItem.id,
                    label: menuItem?.title?.raw || menuItem?.title?.rendered,
                    current: path === router.asPath,
                    url: menuItem.url,
                    children: parseMenu(
                        items.filter(
                            (child) => child?.parent?.toString() === menuItem.id
                        ),
                        parseInt(menuItem.id, 10)
                    ),
                };
            });
    }

    // Create an array of menu items with the children attached to each item as child attribute.
    const menuItems = parseMenu(menu?.items);

    return (
        <React.Fragment>
            {/* Mobile menu panel */}
            {open && (
                <MenuList
                    sx={{
                        boxShadow: '$shadows.shadow100',
                        position: 'absolute',
                        width: '100%',
                        background: '$colors.backgroundWhite',
                        padding: '$spacing.md',
                        top: '141px',
                        left: 0,
                        zIndex: '100',
                        '@media $md_lte': {
                            top: '117px',
                        },
                        '@media (min-width: 890px)': {
                            display: 'none',
                        },
                    }}
                >
                    {[
                        {
                            id: 'home',
                            label: 'Home',
                            current: '/learn/' === router.asPath,
                            url: '/learn/',
                        },
                        ...menuItems,
                    ].map((item) => (
                        <MenuItem
                            key={item.id}
                            item={item}
                            sx={{
                                marginBottom: '$spacing.md',
                                ':last-child': {
                                    marginBottom: '0',
                                },
                            }}
                        />
                    ))}
                </MenuList>
            )}
            <Box
                sx={{
                    boxShadow: '$shadows.shadow300',
                    zIndex: '100',
                    position: 'relative',
                    background: '$colors.backgroundWhite',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        height: '80px',
                        alignItems: 'center',
                        maxWidth: GROUP_WIDTH_MAP.xl,
                        justifyContent: 'space-between',
                        margin: '0 auto',
                        padding: '0 $spacing.sm',
                        '@media $md_lte': {
                            height: '56px',
                        },
                    }}
                >
                    <React.Fragment>
                        <Box
                            sx={{
                                display: 'none',
                                '@media (max-width: 889px)': {
                                    display: 'block',
                                },
                                '@media $md_lte': {
                                    display: !searchOpen ? 'block' : 'none',
                                },
                            }}
                        >
                            <MenuLink
                                onClick={yieldCallback(() => setOpen(!open))}
                                id="menu-toggle"
                            >
                                Learning Center
                                <ToggleIcon
                                    size="xs"
                                    fontColor="textDark"
                                    marginLeft="xs"
                                />
                            </MenuLink>
                        </Box>

                        <MenuList
                            sx={{
                                display: 'flex',
                                height: '100%',
                                '@media (max-width: 889px)': {
                                    display: 'none',
                                },
                            }}
                        >
                            <Box
                                as="li"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginRight: '$spacing.lg',
                                }}
                            >
                                <MenuLink href="/learn/" fontColor="textDark">
                                    Learning Center
                                </MenuLink>
                                <div style={{ height: '32px' }}>
                                    <Divider
                                        orientation="vertical"
                                        length="fluid"
                                        marginLeft="lg"
                                    />
                                </div>
                            </Box>
                            {menuItems.map((item) => (
                                <MenuItem
                                    key={item.id}
                                    item={item}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginRight: '$spacing.lg',
                                        position: 'relative',
                                        ':after': {
                                            content: '""',
                                            display: item.current
                                                ? 'block'
                                                : 'none',
                                            position: 'absolute',
                                            bottom: '0',
                                            left: '0',
                                            width: '100%',
                                            height: '2px',
                                            backgroundColor: '$colors.brand',
                                        },
                                    }}
                                />
                            ))}
                        </MenuList>
                    </React.Fragment>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'stretch',
                            '@media $md_lte': {
                                width: searchOpen ? '100%' : 'auto',
                            },
                        }}
                    >
                        <SearchBox
                            actionPrefix="/learn/search/"
                            onFocus={() => {
                                setSearchOpen(true);
                                setOpen(false);
                            }}
                            onBlur={() => setSearchOpen(false)}
                        />
                    </Box>
                </Box>
            </Box>
        </React.Fragment>
    );
};

export default LearningCenterMenu;
